import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import Layout from "@/components/Layout";
import api, { formatApiError } from "@/lib/api";
import { createCase, updateCase, getCase as getCaseOffline } from "@/lib/offlineApi";
import CodeMultiSelect from "@/components/CodeMultiSelect";
import Autocomplete from "@/components/Autocomplete";
import DatePicker from "@/components/DatePicker";
import StaffPicker from "@/components/StaffPicker";
import { OMFS_CATEGORIES } from "@/lib/omfsCategories";
import { ClipboardList, Users, Stethoscope, FileText, Save } from "lucide-react";

const ROLES = ["Primary Surgeon", "First Assist", "Second Assist", "Teaching Assist", "Observer"];
const SEXES = ["Male", "Female", "Other"];

function Section({ icon: Icon, title, children, testId }) {
  return (
    <div className="neu-card p-6 md:p-8 space-y-5" data-testid={testId}>
      <div className="flex items-center gap-3 mb-2">
        <div className="w-10 h-10 rounded-xl bg-neu-bg flex items-center justify-center shadow-neu-inset-sm">
          <Icon className="w-5 h-5 text-neu-accent" />
        </div>
        <h2 className="heading text-lg md:text-xl text-[#3F4E5B]">{title}</h2>
      </div>
      {children}
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <div>
      <label className="block text-sm font-medium mb-2">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      {children}
    </div>
  );
}

export default function NewCase() {
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const duplicateFromId = searchParams.get("from");
  const editId = searchParams.get("edit");
  const isEditing = !!editId;
  const [categories, setCategories] = useState({});
  const [institutions, setInstitutions] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [originalCase, setOriginalCase] = useState(null);

  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({
    date_of_procedure: today,
    patient_name: "",
    patient_dob: "",
    patient_age: "",
    patient_sex: "",
    procedure_name: "",
    omfs_procedure_category: "",
    cpt_codes: [],
    icd10_codes: [],
    attending_physician: "",
    second_surgeon: "",
    role_in_case: "Primary Surgeon",
    institution: "",
    location: "",
    duration_hours: "",
    notes: "",
    teaching_points: "",
  });

  const set = (k, v) =>
    setForm((f) => {
      const next = { ...f, [k]: v };
      // Auto-calc age from DOB
      if (k === "patient_dob" && v) {
        try {
          const dob = new Date(v);
          const now = new Date();
          let age = now.getFullYear() - dob.getFullYear();
          const m = now.getMonth() - dob.getMonth();
          if (m < 0 || (m === 0 && now.getDate() < dob.getDate())) age -= 1;
          if (age >= 0 && age <= 120) next.patient_age = age;
        } catch { /* ignore */ }
      }
      return next;
    });

  useEffect(() => {
    Promise.all([
      api.get("/procedure-categories"),
      api.get("/institutions/mine"),
    ])
      .then(([c, i]) => {
        setCategories(c.data || {});
        setInstitutions(i.data || []);
      })
      .catch(() => {});
  }, []);

  // Load a source case when duplicating OR editing
  const prefilledFromRef = useRef(null);
  useEffect(() => {
    const sourceId = editId || duplicateFromId;
    if (!sourceId) return;
    if (prefilledFromRef.current === sourceId) return;
    prefilledFromRef.current = sourceId;
    getCaseOffline(sourceId)
      .then((data) => {
        if (!data) throw new Error("not found");
        if (editId) {
          setOriginalCase(data);
          // Full prefill for editing — keep original date and patient details
          setForm({
            date_of_procedure: data.date_of_procedure || today,
            patient_name: data.patient_name || data.patient_identifier || "",
            patient_dob: data.patient_dob || "",
            patient_age: data.patient_age ?? "",
            patient_sex: data.patient_sex || "",
            procedure_name: data.procedure_name || "",
            omfs_procedure_category: data.omfs_procedure_category || "",
            cpt_codes: data.cpt_codes || [],
            icd10_codes: data.icd10_codes || [],
            attending_physician: data.attending_physician || "",
            second_surgeon: data.second_surgeon || "",
            role_in_case: data.role_in_case || "Primary Surgeon",
            institution: data.institution || "",
            location: data.location || "",
            duration_hours: data.duration_minutes
              ? (data.duration_minutes / 60).toString()
              : "",
            notes: data.notes || "",
            teaching_points: data.teaching_points || "",
          });
        } else {
          // Duplicate: reuse procedure setup, clear patient/notes, date=today
          setForm((f) => ({
            ...f,
            date_of_procedure: today,
            patient_name: "",
            patient_dob: "",
            patient_age: "",
            patient_sex: "",
            procedure_name: data.procedure_name || "",
            omfs_procedure_category: data.omfs_procedure_category || "",
            cpt_codes: data.cpt_codes || [],
            icd10_codes: data.icd10_codes || [],
            attending_physician: data.attending_physician || "",
            second_surgeon: data.second_surgeon || "",
            role_in_case: data.role_in_case || "Primary Surgeon",
            institution: data.institution || "",
            location: data.location || "",
            duration_hours: data.duration_minutes
              ? (data.duration_minutes / 60).toString()
              : "",
            notes: "",
            teaching_points: "",
          }));
          toast.success("Prefilled from previous case — update patient details & save");
        }
      })
      .catch(() => toast.error("Could not load source case"));
  }, [duplicateFromId, editId, today]);

  // Auto-derive category from selected procedure name (for analytics/back-compat)
  const inferCategory = (name) => {
    for (const [cat, procs] of Object.entries(categories)) {
      if (procs.includes(name)) return cat;
    }
    return null;
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      const formOverrides = {
        ...form,
        patient_age: form.patient_age ? Number(form.patient_age) : null,
        duration_minutes: form.duration_hours
          ? Math.round(Number(form.duration_hours) * 60)
          : null,
        procedure_category: inferCategory(form.procedure_name) || null,
        omfs_procedure_category: form.omfs_procedure_category || null,
        patient_dob: form.patient_dob || null,
        patient_sex: form.patient_sex || null,
      };
      delete formOverrides.duration_hours;

      // When editing, merge over the ORIGINAL case so legacy fields (procedure_site,
      // approach, complications*, blood_loss_ml, transfusion, etc.) aren't wiped.
      const payload = isEditing && originalCase
        ? { ...originalCase, ...formOverrides }
        : formOverrides;
      // Strip server-side fields that don't belong in the write payload
      delete payload.id;
      delete payload.user_id;
      delete payload.created_at;
      delete payload.updated_at;

      if (isEditing) {
        const res = await updateCase(editId, payload);
        toast.success(res?._pending ? "Case saved locally — will sync when online" : "Case updated");
      } else {
        const res = await createCase(payload);
        toast.success(res?._pending ? "Case saved locally — will sync when online" : "Case logged");
      }
      nav("/cases");
    } catch (err) {
      setError(formatApiError(err, "Could not save case"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Layout>
      <div className="mb-8">
        <h1 className="heading text-3xl md:text-4xl text-[#3F4E5B]">
          {isEditing
            ? "Edit case"
            : duplicateFromId
            ? "Log a new case (from previous)"
            : "Log a new case"}
        </h1>
        <p className="subtle mt-2">
          {isEditing
            ? "Update any field, then save your changes."
            : "Capture the details while it\u2019s still fresh."}
        </p>
      </div>

      <form onSubmit={submit} className="space-y-6" data-testid="new-case-form">
        <Section icon={ClipboardList} title="Basic info" testId="section-basic">
          <div className="grid md:grid-cols-2 gap-5">
            <Field label="Date of procedure" required>
              <DatePicker
                value={form.date_of_procedure}
                onChange={(v) => set("date_of_procedure", v)}
                max={today}
                testId="field-date"
              />
            </Field>
            <Field label="Patient name" required>
              <input
                required
                value={form.patient_name}
                onChange={(e) => set("patient_name", e.target.value)}
                placeholder="e.g. John Smith"
                className="neu-input"
                data-testid="field-patient-name"
              />
            </Field>
            <Field label="Patient date of birth">
              <DatePicker
                value={form.patient_dob}
                onChange={(v) => set("patient_dob", v)}
                max={today}
                testId="field-dob"
              />
            </Field>
            <Field label="Patient age">
              <input
                type="number"
                min={0}
                max={120}
                value={form.patient_age}
                onChange={(e) => set("patient_age", e.target.value)}
                className="neu-input"
                data-testid="field-age"
              />
            </Field>
            <Field label="Patient sex">
              <select
                value={form.patient_sex}
                onChange={(e) => set("patient_sex", e.target.value)}
                className="neu-input"
                data-testid="field-sex"
              >
                <option value="">—</option>
                {SEXES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </Field>
          </div>
        </Section>

        <Section icon={Stethoscope} title="Procedure" testId="section-procedure">
          <Field label="Procedure name" required>
            <input
              required
              type="text"
              value={form.procedure_name}
              onChange={(e) => set("procedure_name", e.target.value)}
              placeholder="e.g. LeFort I osteotomy, ORIF mandible, cholecystectomy"
              className="neu-input"
              data-testid="field-procedure-name"
              autoComplete="off"
            />
          </Field>
          <Field label="OMFS category">
            <select
              value={form.omfs_procedure_category}
              onChange={(e) => set("omfs_procedure_category", e.target.value)}
              className="neu-input"
              data-testid="field-omfs-category"
            >
              <option value="">All categories</option>
              {OMFS_CATEGORIES.map((c) => (
                <option key={c} value={c} data-testid={`field-omfs-category-opt-${c}`}>
                  {c}
                </option>
              ))}
            </select>
          </Field>
          <CodeMultiSelect
            label="CPT codes"
            type="cpt"
            value={form.cpt_codes}
            onChange={(v) => set("cpt_codes", v)}
            max={10}
            testId="field-cpt"
          />
          <CodeMultiSelect
            label="ICD-10 codes"
            type="icd10"
            value={form.icd10_codes}
            onChange={(v) => set("icd10_codes", v)}
            max={5}
            testId="field-icd10"
          />
        </Section>

        <Section icon={Users} title="Team" testId="section-team">
          <div className="grid md:grid-cols-2 gap-5">
            <div>
              <label className="block text-sm font-medium mb-2">
                Attending physician <span className="text-red-500">*</span>
              </label>
              <StaffPicker
                value={form.attending_physician}
                onChange={(v) => set("attending_physician", v)}
                required
                testId="field-attending"
              />
            </div>
            <Field label="Role in case" required>
              <select
                required
                value={form.role_in_case}
                onChange={(e) => set("role_in_case", e.target.value)}
                className="neu-input"
                data-testid="field-role"
              >
                {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
            </Field>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium mb-2">
                2nd surgeon <span className="subtle text-xs">(optional)</span>
              </label>
              <StaffPicker
                value={form.second_surgeon}
                onChange={(v) => set("second_surgeon", v)}
                hideAdd
                testId="field-second-surgeon"
              />
            </div>
            <Autocomplete
              label="Institution"
              required
              value={form.institution}
              onChange={(v) => set("institution", v)}
              options={institutions}
              placeholder="University Medical Center"
              testId="field-institution"
            />
            <Field label="Case duration (hours)">
              <input
                type="number"
                min={0}
                max={12}
                step={0.25}
                value={form.duration_hours}
                onChange={(e) => set("duration_hours", e.target.value)}
                placeholder="e.g. 2.5"
                className="neu-input"
                data-testid="field-duration"
              />
            </Field>
          </div>
        </Section>

        <Section icon={FileText} title="Notes & teaching" testId="section-notes">
          <Field label="Notes">
            <textarea
              rows={3}
              value={form.notes}
              onChange={(e) => set("notes", e.target.value)}
              className="neu-input py-3 resize-none"
              style={{ height: "auto", minHeight: "5rem" }}
              data-testid="field-notes"
            />
          </Field>
          <Field label="Teaching points / what I learned">
            <textarea
              rows={3}
              value={form.teaching_points}
              onChange={(e) => set("teaching_points", e.target.value)}
              className="neu-input py-3 resize-none"
              style={{ height: "auto", minHeight: "5rem" }}
              data-testid="field-teaching"
            />
          </Field>
        </Section>

        {error && (
          <div className="text-sm text-red-600 bg-red-50 p-3 rounded-xl" data-testid="form-error">
            {error}
          </div>
        )}

        <div className="flex flex-wrap items-center gap-3 justify-end sticky bottom-4 z-10">
          <button
            type="button"
            className="btn-neu"
            onClick={() => nav(-1)}
            data-testid="new-case-cancel"
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn-accent inline-flex items-center gap-2"
            disabled={submitting}
            data-testid="new-case-submit"
          >
            <Save className="w-4 h-4" />
            {submitting ? "Saving\u2026" : isEditing ? "Save changes" : "Log case"}
          </button>
        </div>
      </form>
    </Layout>
  );
}
